import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterModule,
  Routes,
} from "@angular/router";
import { map, take } from "rxjs/operators";
import { LoggedInGuard } from "../auth/auth.guards";
import { BeaconsListComponent } from "./beacons-list/beacons-list.component";
import { SessionService } from "../session.service";
import { BeaconService } from "../beacons/beacon.service";
import {
  FacilitiesListGuard,
  FacilitiesResolve,
} from "../facilities/facilities.routes";
import { BeaconComponent } from "./beacon/beacon.component";
import { BeaconRegionsResolve } from "../broadcasts/broadcasts.routes";
import { PermissionService } from "../permission.service";
import { BeaconRegionListGuard } from "../beacon-regions/beacon-regions.guards";
import {
  BeaconsDetailGuard,
  BeaconsListGuard,
  BeaconsWriteGuard,
} from "./beacons.guards";

@Injectable({ providedIn: "root" })
class BeaconsListPermissionsResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    return Promise.all([
      this.PermissionService.categories.beacons.hasWrite(),
      this.PermissionService.categories.beacons.hasDetail(),
      this.PermissionService.categories.facilities.hasList(),
      this.PermissionService.categories.beaconRegions.hasList(),
    ]).then(
      ([
        hasBeaconsWrite,
        hasBeaconsDetail,
        hasFacilitiesList,
        hasBeaconRegionsList,
      ]) => {
        return {
          hasBeaconsWrite,
          hasBeaconsDetail,
          hasFacilitiesList,
          hasBeaconRegionsList,
        };
      },
    );
  }
}

@Injectable({ providedIn: "root" })
export class BeaconsResolve implements Resolve<any> {
  constructor(
    private BeaconService: BeaconService,
    private SessionService: SessionService,
  ) {}
  resolve() {
    return this.BeaconService.getBeacons(
      this.SessionService.currentAccountValue!.accountID,
      {
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class BeaconResolve implements Resolve<any> {
  constructor(
    private BeaconService: BeaconService,
    private SessionService: SessionService,
  ) {}
  resolve(route: ActivatedRouteSnapshot) {
    return this.BeaconService.getBeacon(
      this.SessionService.currentAccountValue!.accountID,
      route.params.beaconID,
    ).pipe(take(1));
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard],
    runGuardsAndResolvers: "always",
    data: { sidebar: "infrastructure" },
    children: [
      {
        path: "list",
        component: BeaconsListComponent,
        resolve: {
          // beacons: BeaconsResolve,
          permissions: BeaconsListPermissionsResolve,
        },
        data: { title: "Beacons" },
        canActivate: [BeaconsListGuard],
      },
      {
        path: "create",
        component: BeaconComponent,
        resolve: {
          facilities: FacilitiesResolve,
          beaconRegions: BeaconRegionsResolve,
        },
        canActivate: [
          BeaconsWriteGuard,
          FacilitiesListGuard,
          BeaconRegionListGuard,
        ],
        data: { title: "Create New Beacon" },
      },
      {
        path: ":beaconID",
        resolve: {
          beacon: BeaconResolve,
          facilities: FacilitiesResolve,
          beaconRegions: BeaconRegionsResolve,
        },
        canActivate: [
          BeaconsWriteGuard,
          BeaconsDetailGuard,
          FacilitiesListGuard,
          BeaconRegionListGuard,
        ],

        runGuardsAndResolvers: "paramsChange",
        children: [
          {
            path: "",
            component: BeaconComponent,
            data: { title: "Beacon" },
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class BeaconsRoutingModule {}

const beaconsModuleLazyLoadRoutes: Routes = [
  {
    path: "beacons",
    loadChildren: () => import("./beacons.module").then((m) => m.BeaconsModule),
  },
];

export { beaconsModuleLazyLoadRoutes };
